import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PersonaDoc } from '@/dao/firestore/PersonasRepository';
import { AudienceCharts } from './AudienceCharts';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Users, MapPin, Briefcase, DollarSign, Play, Pause, Settings, Clock, MessageSquare, Star, CreditCard } from "lucide-react";

import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { Card, CardContent } from '../ui/card';
import { Button } from "@/components/ui/button";
import { getAudienceSlides } from './AudienceCharts';
import { useState } from 'react';





interface AudienceModalProps {
  open: boolean;
  selectedPersonas: PersonaDoc[];
  onClose: () => void;
  personas: PersonaDoc[];
}

export function AudienceModal({ 
  open, 
  onClose, 
  selectedPersonas,
  personas 
}: AudienceModalProps) {
  
  const [selectedPersonaModal, setSelectedPersonaModal] = useState<PersonaDoc | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hoveredFilter, setHoveredFilter] = useState<{type: string, value: string} | null>(null);

  const { t } = useTranslation();

  // Function to check if a persona matches the current hover filter
  const personaMatchesFilter = (persona: PersonaDoc) => {
    if (!hoveredFilter) return true;
    
    switch (hoveredFilter.type) {
      case 'age':
        const ageRange = hoveredFilter.value;
        const age = persona.age || 0;
        if (ageRange === '18-24') return age >= 18 && age < 25;
        if (ageRange === '25-34') return age >= 25 && age < 35;
        if (ageRange === '35-44') return age >= 35 && age < 45;
        if (ageRange === '45-54') return age >= 45 && age < 55;
        if (ageRange === '55+') return age >= 55;
        break;
      case 'gender':
        return persona.gender === hoveredFilter.value;
      case 'country':
        return persona.location?.country === hoveredFilter.value;
      case 'city':
        return persona.location?.city === hoveredFilter.value;
      case 'income':
        const income = persona.estimated_income_eur || 0;
        const range = hoveredFilter.value;
        if (range === t('audience.incomeRanges.low')) return income < 30000;
        if (range === t('audience.incomeRanges.medium')) return income >= 30000 && income < 50000;
        if (range === t('audience.incomeRanges.high')) return income >= 50000 && income < 80000;
        if (range === t('audience.incomeRanges.veryHigh')) return income >= 80000;
        break;
      case 'occupation':
        const occupations = persona.occupation?.split(/[,/;]/).map(o => o.trim()) || [];
        return occupations.some(occ => 
          occ === hoveredFilter.value || 
          (occ.length > 20 && hoveredFilter.value.includes('...') && occ.startsWith(hoveredFilter.value.replace('...', '')))
        );
      case 'platform':
        return persona.preferred_platforms?.includes(hoveredFilter.value) || false;
      case 'tech':
        const techLevel = persona.technological_proficiency?.split('(')[0].toLowerCase().trim();
        return techLevel === hoveredFilter.value;
      default:
        return true;
    }
    return true;
  };


  const expandedAudience = true;
  const audienceSlides = getAudienceSlides(selectedPersonas, t, hoveredFilter, setHoveredFilter);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl  ">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('screeningRoom.audience.title')}
          </DialogTitle>
        </DialogHeader>



        <Card>
          <CardContent className="p-3">

                    {/* One-line Audience Summary */}
                    <div className="flex items-center justify-between p-1 bg-muted/30 rounded-lg mb-4">
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-primary" />
                          <span className="font-medium">{selectedPersonas.length} {t('screeningRoom.audienceSummary.personas')}</span>
                        </div>
                         <div className="flex items-center gap-2">
                           <span>{t('screeningRoom.audienceSummary.averageAge')}</span>
                           <span className="font-medium text-primary">
                             {Math.round(selectedPersonas.reduce((sum, p) => sum + (p.age || 0), 0) / selectedPersonas.length)} {t('screeningRoom.audienceSummary.years')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium text-primary">
                            {new Set(selectedPersonas.map(p => p.location?.city).filter(Boolean)).size} {t('screeningRoom.audienceSummary.cities')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium text-primary">
                            {Math.round(selectedPersonas.reduce((sum, p) => sum + (p.estimated_income_eur || 0), 0) / selectedPersonas.length / 1000)}k€ {t('screeningRoom.audienceSummary.averageIncome')}
                          </span>
                        </div>
                      </div>
                    </div>

        </CardContent></Card>

        <div className="max-h-[50vh] overflow-y-auto">
          {audienceSlides.map((slide, index) => (
            <div key={index} className="p-6 m-3 border-0">
              {slide?.content}
            </div>
          ))}
        </div>

        <Card>
          <CardContent className="p-6">
                            <h5 className="text-sm font-medium mb-3">{t('screeningRoom.audienceTitle')}</h5>
                            <div className="flex flex-wrap gap-3  overflow-y-auto">
                              {selectedPersonas.map((persona, index) => {
                                const matches = personaMatchesFilter(persona);
                                return (
                                  <div
                                    key={persona.id || index}
                                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-muted/50 transition-all duration-200 ${
                                      matches ? 'opacity-100 bg-primary/10 border-primary/20' : 'opacity-10'
                                    }`}
                                    onClick={() => setSelectedPersonaModal(persona)}
                                    title={t('studio.critics.personaInformation', { name: persona.name })}
                                  >
                                    <Avatar className="w-8 h-8">
                                      <AvatarImage src={persona.image || '/placeholder.svg'} alt={persona.name} />
                                      <AvatarFallback className="text-xs">
                                        {persona.name?.charAt(0) || t('studio.critics.unknownUser')}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm font-medium whitespace-nowrap">{persona.name || t('studio.critics.unknownName')}</span>
                                  </div>
                                );
                              })}
                            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}